import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createAdminTeam,
  createAdminSeason,
  createTeamInvite,
  updateAdminSettings,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { slugify } from "@/lib/slug";
import { publicAppSettingsKey, unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * M9b D1–D9 — `/welcome` 3-step onboarding wizard.
 *
 * Auto-fires (via `useWelcomeRedirect()` at the app shell) for new
 * global admins on a freshly-installed tenant (zero teams, not
 * previously skipped). Always reachable manually at `/welcome`.
 *
 * Four step states (matches legacy `WELCOME_STEPS`):
 *   1. team    — Create the first team (POST /api/admin/teams).
 *   2. season  — Create the first shared season (POST /api/admin/seasons).
 *                Seasons are global — not bound to the team above.
 *   3. invite  — Send coach invites (POST /api/team/invites); shows
 *                a running list + a dev-token Copy-link when the
 *                backend response carries `invite_token`.
 *   4. done    — Personalized confirmation + CTA → /admin/people.
 *
 * Skip-at-any-step sets `localStorage.tktic_welcome_skipped=1` and
 * routes to `/admin/teams` so the operator can finish setup
 * manually. Step state lives entirely in component memory — closing
 * the browser mid-wizard does NOT resume (matches legacy; 🔵 step
 * persistence is deferred).
 *
 * Privacy invariant: invite tokens surface ONLY when the backend
 * returns one (TKTIC_DEV_TOKEN_DELIVERY=1), never logged, always
 * behind a gated Copy-link control.
 */

const WELCOME_STEPS = ["team", "season", "invite", "done"] as const;
type WelcomeStep = (typeof WELCOME_STEPS)[number];

const ROLE_OPTIONS = [
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "team_admin", label: "Team admin" },
];

const GAME_FORMAT_OPTIONS = [
  { value: "11v11", label: "11 v 11 (full)" },
  { value: "9v9", label: "9 v 9" },
  { value: "7v7", label: "7 v 7" },
];

const SKIP_FLAG = "tktic_welcome_skipped";

type CreatedTeam = { id: string; name: string; slug: string };
type CreatedSeason = { id: string; name: string };
type SentInvite = {
  email: string;
  role: string;
  inviteUrl: string | null;
};

export function WelcomeShell() {
  const me = useMe();
  const navigate = useNavigate({ from: "/welcome" });

  // D8 auth gate — global admin required. Non-admins bounce to /.
  // useRef guard mirrors the M9a auth-gate pattern.
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (me.isLoading) return;
    if (authRedirectFired.current) return;
    if (!me.data?.user) {
      authRedirectFired.current = true;
      void navigate({
        to: "/login",
        search: { next: "/welcome" },
        replace: true,
      });
      return;
    }
    if (!me.data.user.is_global_admin) {
      authRedirectFired.current = true;
      void navigate({ to: "/", replace: true });
    }
  }, [me.isLoading, me.data, navigate]);

  const [step, setStep] = useState<WelcomeStep>("team");
  const [team, setTeam] = useState<CreatedTeam | null>(null);
  const [season, setSeason] = useState<CreatedSeason | null>(null);
  const [invites, setInvites] = useState<SentInvite[]>([]);

  // D7 skip-at-any-step. Persists the suppress flag (so the
  // auto-redirect hook stops firing) and lands on /admin/teams.
  const skipFlow = () => {
    try {
      localStorage.setItem(SKIP_FLAG, "1");
    } catch {
      // ignore — quota / private-mode failures are non-fatal
    }
    void navigate({ to: "/admin/teams", replace: true });
  };

  // D6 finish — set the skip flag (so re-loading /welcome doesn't
  // restart the wizard) and advance to the personalized done card.
  const finishFlow = () => {
    try {
      localStorage.setItem(SKIP_FLAG, "1");
    } catch {
      // ignore
    }
    setStep("done");
  };

  if (me.isLoading) {
    return (
      <main
        data-testid="welcome-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }
  if (!me.data?.user || !me.data.user.is_global_admin) {
    return null;
  }

  return (
    <main
      data-testid="welcome-page"
      className="min-h-screen bg-canvas"
    >
      <header className="px-4 py-3 border-b border-rule flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-sm font-display uppercase tracking-wider text-ink">
            Welcome to Tktic
          </h1>
          <p className="text-ink-muted text-xs">
            Three quick steps and you&rsquo;re ready to coach.
          </p>
        </div>
        {step !== "done" ? (
          <button
            type="button"
            onClick={skipFlow}
            data-testid="welcome-skip"
            className="text-xs text-ink-muted hover:text-ink hover:underline"
          >
            Skip setup
          </button>
        ) : null}
      </header>

      <StepIndicator current={step} />

      <section className="p-4 max-w-2xl mx-auto">
        {step === "team" ? (
          <TeamStep
            onCreated={(t) => {
              setTeam(t);
              setStep("season");
            }}
          />
        ) : null}
        {step === "season" && team ? (
          <SeasonStep
            team={team}
            onCreated={(s) => {
              setSeason(s);
              setStep("invite");
            }}
            onSkip={() => setStep("invite")}
          />
        ) : null}
        {step === "invite" && team ? (
          <InviteStep
            team={team}
            invites={invites}
            onAdded={(inv) => setInvites((prev) => [...prev, inv])}
            onFinish={finishFlow}
          />
        ) : null}
        {step === "done" ? (
          <DoneStep
            team={team}
            season={season}
            inviteCount={invites.length}
            onContinue={() =>
              void navigate({ to: "/admin/people", replace: true })
            }
          />
        ) : null}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// D1 — Step indicator
// ---------------------------------------------------------------------------

function StepIndicator({ current }: { current: WelcomeStep }) {
  const currentIndex = WELCOME_STEPS.indexOf(current);
  return (
    <nav
      data-testid="welcome-steps"
      role="list"
      aria-label="Onboarding steps"
      className="px-4 py-3 flex items-center gap-3 border-b border-rule"
    >
      {WELCOME_STEPS.map((s, idx) => {
        const isCurrent = s === current;
        const isDone = idx < currentIndex;
        return (
          <div
            key={s}
            role="listitem"
            data-testid={`welcome-step-${s}`}
            data-state={isCurrent ? "current" : isDone ? "done" : "upcoming"}
            aria-current={isCurrent ? "step" : undefined}
            className="flex items-center gap-1.5 text-xs font-mono"
          >
            <span
              className={
                "inline-flex h-5 w-5 items-center justify-center rounded-full text-[0.65rem] font-display " +
                (isCurrent
                  ? "bg-accent text-accent-ink"
                  : isDone
                    ? "bg-good text-paper"
                    : "border border-rule bg-canvas text-ink-muted")
              }
            >
              {idx + 1}
            </span>
            <span
              className={
                isCurrent
                  ? "text-ink"
                  : isDone
                    ? "text-ink-muted line-through"
                    : "text-ink-muted"
              }
            >
              {labelForStep(s)}
            </span>
          </div>
        );
      })}
    </nav>
  );
}

function labelForStep(s: WelcomeStep): string {
  switch (s) {
    case "team":
      return "Create team";
    case "season":
      return "Add season";
    case "invite":
      return "Invite coaches";
    case "done":
      return "Done";
  }
}

// ---------------------------------------------------------------------------
// D3 — Step 1: Create team
// ---------------------------------------------------------------------------

function TeamStep({ onCreated }: { onCreated: (team: CreatedTeam) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [gameFormat, setGameFormat] = useState("11v11");
  const [clubTitle, setClubTitle] = useState("");
  const [clubTitleTouched, setClubTitleTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
    // Club title mirrors the team name until the operator edits it
    // explicitly. Matches the slug-auto-derive UX in this same step.
    if (!clubTitleTouched) {
      setClubTitle(value);
    }
  };

  const mut = useMutation({
    mutationFn: async () => {
      const team = await createAdminTeam({
        name: name.trim(),
        slug: slug.trim(),
        game_format: gameFormat,
      });
      // Club title is the public season-header label rendered across
      // every public surface (landing, /teams). On a fresh install
      // settings.py defaults this to "New Club"; wiring it in here
      // means the operator never has to bounce to /admin/settings
      // post-onboarding just to brand their public pages. Failure
      // here is non-fatal — the team still got created, and the
      // operator can edit later.
      const title = clubTitle.trim();
      if (title) {
        try {
          await updateAdminSettings({ club_title: title });
          // Invalidate the admin-settings + public app-settings caches
          // so any already-mounted SettingsPage / public header picks
          // up the new title on its next render instead of showing
          // the prior default for up to the 30 s staleTime window.
          // Mirrors the invalidation SettingsPage's own save mutation
          // fires after a PUT.
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: unscopedKey("admin-settings"),
            }),
            queryClient.invalidateQueries({
              queryKey: publicAppSettingsKey(),
            }),
          ]);
        } catch {
          // Swallow — the wizard advances and the operator can
          // correct the title from /admin/settings.
        }
      }
      return team;
    },
    onSuccess: (resp) => {
      onCreated({ id: resp.id, name: resp.name, slug: resp.slug });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError("A team with that slug already exists. Pick another slug.");
      } else {
        setError(formatErr(err));
      }
    },
  });

  return (
    <form
      data-testid="welcome-team-form"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!name.trim()) {
          setError("Team name is required.");
          return;
        }
        if (!slug.trim()) {
          setError("Slug is required.");
          return;
        }
        mut.mutate();
      }}
      className="flex flex-col gap-3"
    >
      <h2 className="text-base font-display uppercase tracking-wider text-ink">
        Create your first team
      </h2>
      <p className="text-xs text-ink-muted">
        Teams are the top-level container for seasons, rosters, and coaching
        content.
      </p>

      <TextField
        label="Team name"
        value={name}
        onChange={onNameChange}
        testid="welcome-team-name"
        autoFocus
        maxLength={120}
      />
      <TextField
        label="Slug"
        value={slug}
        onChange={(v) => {
          setSlug(v);
          setSlugTouched(true);
        }}
        testid="welcome-team-slug"
        maxLength={64}
        help="URL-safe identifier. Auto-derived from the name; edit to override."
      />
      <TextField
        label="Club title"
        value={clubTitle}
        onChange={(v) => {
          setClubTitle(v);
          setClubTitleTouched(true);
        }}
        testid="welcome-team-club-title"
        maxLength={120}
        help="Public heading shown across landing + team pages. Auto-mirrors the team name; edit to override."
      />

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Game format
        </span>
        <TkticSelect
          value={gameFormat}
          onChange={(next) => setGameFormat(next)}
          data-testid="welcome-team-game-format"
          ariaLabel="Game format"
          options={GAME_FORMAT_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
        />
      </label>

      {error ? (
        <p
          data-testid="welcome-team-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={mut.isPending}
        data-testid="welcome-team-submit"
        className="self-start"
      >
        {mut.isPending ? "Creating…" : "Create team"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// D4 — Step 2: Create season
// ---------------------------------------------------------------------------

function SeasonStep({
  team,
  onCreated,
  onSkip,
}: {
  team: CreatedTeam;
  onCreated: (season: CreatedSeason) => void;
  onSkip: () => void;
}) {
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createAdminSeason({
        name: name.trim(),
        starts_on: startsOn,
        ends_on: endsOn,
      }),
    onSuccess: (resp) => {
      onCreated({ id: resp.id, name: resp.name });
    },
    onError: (err) => {
      setError(formatErr(err));
    },
  });

  return (
    <form
      data-testid="welcome-season-form"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!name.trim()) {
          setError("Season name is required.");
          return;
        }
        if (startsOn && endsOn && startsOn > endsOn) {
          setError("End date must be on or after the start date.");
          return;
        }
        mut.mutate();
      }}
      className="flex flex-col gap-3"
    >
      <h2 className="text-base font-display uppercase tracking-wider text-ink">
        Add your first season
      </h2>
      <p className="text-xs text-ink-muted">
        A season groups matches, rosters, and coaching content for a chunk of
        time (e.g. Spring 2026, U12 league). Seasons are shared across every
        team — {team.name} and any teams you add later all reference the same
        season list.
      </p>

      <TextField
        label="Season name"
        value={name}
        onChange={setName}
        testid="welcome-season-name"
        autoFocus
        maxLength={120}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Starts on
          </span>
          <TkticDateInput
            value={startsOn}
            onChange={setStartsOn}
            data-testid="welcome-season-starts-on"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Ends on
          </span>
          <TkticDateInput
            value={endsOn}
            onChange={setEndsOn}
            data-testid="welcome-season-ends-on"
          />
        </label>
      </div>

      {error ? (
        <p
          data-testid="welcome-season-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button
          type="submit"
          disabled={mut.isPending}
          data-testid="welcome-season-submit"
        >
          {mut.isPending ? "Creating…" : "Create season"}
        </Button>
        <button
          type="button"
          onClick={onSkip}
          data-testid="welcome-season-skip"
          className="rounded border border-rule px-3 py-2 text-sm font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink"
        >
          Skip
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// D5 — Step 3: Invite coaches
// ---------------------------------------------------------------------------

function InviteStep({
  team,
  invites,
  onAdded,
  onFinish,
}: {
  team: CreatedTeam;
  invites: SentInvite[];
  onAdded: (inv: SentInvite) => void;
  onFinish: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coach");
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createTeamInvite({
        team_id: team.id,
        email: email.trim(),
        role,
        player_ids: [],
      }),
    onSuccess: (resp) => {
      const inviteUrl = resp.invite_token
        ? buildInviteUrl(resp.invite_token)
        : null;
      onAdded({ email: email.trim(), role, inviteUrl });
      setSuccess(`Invite queued for ${email.trim()}. Add another or finish.`);
      setError(null);
      setEmail("");
      // Chain-invites: focus back to the email input.
      window.setTimeout(() => emailRef.current?.focus(), 0);
    },
    onError: (err) => {
      setSuccess(null);
      setError(formatErr(err));
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-base font-display uppercase tracking-wider text-ink">
        Invite coaches to {team.name}
      </h2>
      <p className="text-xs text-ink-muted">
        We&rsquo;ll email each coach a sign-up link. Add as many as you like —
        you can finish whenever you&rsquo;re done.
      </p>

      <form
        data-testid="welcome-invite-form"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setSuccess(null);
          if (!email.trim() || email.trim().length < 3) {
            setError("A valid email is required.");
            return;
          }
          mut.mutate();
        }}
        className="flex flex-col gap-2"
      >
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-display uppercase tracking-wider text-ink-muted">
              Email
            </span>
            <input
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="off"
              data-testid="welcome-invite-email"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-display uppercase tracking-wider text-ink-muted">
              Role
            </span>
            <TkticSelect
              value={role}
              onChange={(next) => setRole(next)}
              data-testid="welcome-invite-role"
              ariaLabel="Role"
              options={ROLE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
            />
          </label>
        </div>

        {error ? (
          <p
            data-testid="welcome-invite-error"
            className="text-destructive text-xs font-mono"
          >
            {error}
          </p>
        ) : null}
        {success ? (
          <p
            data-testid="welcome-invite-success"
            className="text-good text-xs font-mono"
          >
            {success}
          </p>
        ) : null}

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={mut.isPending}
            data-testid="welcome-invite-submit"
          >
            {mut.isPending ? "Sending…" : "Send invite"}
          </Button>
          <button
            type="button"
            onClick={onFinish}
            data-testid="welcome-invite-finish"
            className="rounded border border-rule px-3 py-2 text-sm font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink"
          >
            {invites.length > 0 ? "Finish setup" : "Skip"}
          </button>
        </div>
      </form>

      {invites.length > 0 ? (
        <div
          data-testid="welcome-invites-sent"
          className="flex flex-col gap-1 rounded border border-rule bg-elevated p-2"
        >
          <p className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
            Invites sent ({invites.length})
          </p>
          <ul className="flex flex-col gap-1" role="list">
            {invites.map((inv, idx) => (
              <li
                key={`${inv.email}-${idx}`}
                data-testid={`welcome-invite-row-${idx}`}
                className="flex items-center gap-2 text-xs font-mono"
              >
                <span className="text-ink">{inv.email}</span>
                <span className="text-ink-muted">
                  · {inv.role.replace(/_/g, " ")}
                </span>
                {inv.inviteUrl ? (
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard?.writeText(inv.inviteUrl!);
                    }}
                    data-testid={`welcome-invite-copy-${idx}`}
                    className="ml-auto text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:underline"
                  >
                    Copy link
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// D6 — Step 4: Done (personalized)
// ---------------------------------------------------------------------------

function DoneStep({
  team,
  season,
  inviteCount,
  onContinue,
}: {
  team: CreatedTeam | null;
  season: CreatedSeason | null;
  inviteCount: number;
  onContinue: () => void;
}) {
  const title = team?.name ? `${team.name} is ready` : "You're all set";
  const seasonBit = season ? ` Your "${season.name}" season is set up too.` : "";
  const inviteBit =
    inviteCount > 0
      ? ` ${inviteCount} invite${inviteCount === 1 ? "" : "s"} on the way.`
      : "";

  return (
    <div
      data-testid="welcome-done"
      className="flex flex-col gap-3"
    >
      <h2
        data-testid="welcome-done-title"
        className="text-lg font-display tracking-tight text-ink"
      >
        {title}
      </h2>
      <p
        data-testid="welcome-done-sub"
        className="text-sm text-ink-muted"
      >
        Head over to the team admin to invite more people, add players, or set
        up matches.{seasonBit}
        {inviteBit}
      </p>
      <Button
        type="button"
        onClick={onContinue}
        data-testid="welcome-done-continue"
        className="self-start"
      >
        Take me to the team admin
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function TextField({
  label,
  value,
  onChange,
  testid,
  autoFocus,
  maxLength,
  help,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  testid: string;
  autoFocus?: boolean;
  maxLength?: number;
  help?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        maxLength={maxLength}
        data-testid={testid}
        className="rounded border border-rule bg-surface px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
      />
      {help ? (
        <span className="text-[0.6rem] font-mono text-ink-muted">{help}</span>
      ) : null}
    </label>
  );
}

function buildInviteUrl(token: string): string {
  if (typeof window === "undefined") return `/invite/${token}`;
  return `${window.location.origin}/invite/${encodeURIComponent(token)}`;
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}
